import { Instagram, Sparkles, Users, Award, Mic2, Mail } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen py-16 px-4" data-testid="about-page">
      {/* Hero Section */}
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#ffffff' }}>
            About <span style={{ color: '#D4AF37' }}>Blendonics</span>
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#a3a3a3' }}>
            Where passion meets profession. Empowering the next generation of DJs.
          </p>
        </div>

        {/* Mission Section */}
        <section className="mb-20">
          <div className="glass-panel rounded-2xl p-8 md:p-12 border relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }} />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                  <Sparkles size={28} style={{ color: '#D4AF37' }} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#D4AF37' }}>Our Mission</h2>
              </div>
              <p className="text-lg leading-relaxed" style={{ color: '#e5e5e5' }}>
                At Blendonics, our mission is to <strong style={{ color: '#D4AF37' }}>empower aspiring DJs</strong> to turn their passion for music into a confident and vibrant career.
              </p>
              <p className="text-lg leading-relaxed mt-4" style={{ color: '#a3a3a3' }}>
                We strive to provide aspiring DJs with the knowledge, skills, and community support they need to navigate the world of DJing.
              </p>
            </div>
          </div>
        </section>

        {/* Instructor Section */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <Mic2 size={28} style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#ffffff' }}>Our Instructor</h2>
          </div>
          
          <div className="glass-panel rounded-2xl border overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image/Visual Side */}
              <div className="p-8 md:p-12 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(58, 0, 136, 0.3) 0%, rgba(212, 175, 55, 0.1) 100%)' }}>
                <div className="text-center">
                  <div className="w-48 h-48 mx-auto rounded-full border-4 overflow-hidden mb-6" style={{ borderColor: '#D4AF37' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_eacf6cef-4114-4eb0-be53-ebdcd9b6bf0e/artifacts/3ocp2t2w_Press%20Shot%202025.JPG" 
                      alt="Elliott Rock - CEO & Founder of Blendonics"
                      className="w-full h-full object-cover object-top"
                      style={{ objectPosition: 'center 15%' }}
                    />
                  </div>
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#ffffff' }}>Elliott Rock</h3>
                  <p className="text-sm font-medium mb-4" style={{ color: '#D4AF37' }}>CEO & Founder</p>
                  <a 
                    href="https://instagram.com/djelliottrock" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all hover:scale-105"
                    style={{ background: 'linear-gradient(45deg, #833AB4, #FD1D1D, #F77737)', color: '#ffffff' }}
                    data-testid="instructor-instagram-link"
                  >
                    <Instagram size={18} />
                    @djelliottrock
                  </a>
                </div>
              </div>
              
              {/* Bio Side */}
              <div className="p-8 md:p-12">
                <div className="space-y-4" style={{ color: '#e5e5e5' }}>
                  <p className="leading-relaxed">
                    <strong style={{ color: '#D4AF37' }}>Elliott Rock</strong>, the driving force behind Blendonics, has over <strong style={{ color: '#ffffff' }}>10 years of experience</strong> as a Professional DJ, transforming his passion into a profession.
                  </p>
                  <p className="leading-relaxed">
                    Inspired by local DJs, he taught himself through hours of practice, landing <strong style={{ color: '#ffffff' }}>six weekly international residencies</strong> in Marbella and Ibiza.
                  </p>
                  <p className="leading-relaxed">
                    Elliott has performed alongside notable artists like <span style={{ color: '#D4AF37' }}>Central Cee, Travis Scott, Gunna, French Montana,</span> and <span style={{ color: '#D4AF37' }}>Trey Songz</span> and regularly performs in major cities such as <strong style={{ color: '#ffffff' }}>London and Dubai</strong>.
                  </p>
                  <p className="leading-relaxed" style={{ color: '#a3a3a3' }}>
                    His journey equips him with valuable insights, which he is dedicated to sharing with the next generation of DJs.
                  </p>
                </div>
                
                {/* Performance Highlights */}
                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(212, 175, 55, 0.05)' }}>
                    <div className="text-2xl font-bold" style={{ color: '#D4AF37' }}>10+</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>Years Experience</div>
                  </div>
                  <div className="text-center p-4 rounded-xl" style={{ background: 'rgba(212, 175, 55, 0.05)' }}>
                    <div className="text-2xl font-bold" style={{ color: '#D4AF37' }}>6</div>
                    <div className="text-xs" style={{ color: '#a3a3a3' }}>Weekly Residencies</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Blendonics Section */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <Award size={28} style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#ffffff' }}>Why <span style={{ color: '#D4AF37' }}>Blendonics</span>?</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-panel rounded-2xl p-8 border text-center">
              <div className="text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>"Blend"</div>
              <p className="text-lg" style={{ color: '#e5e5e5' }}>
                Represents the art of <strong style={{ color: '#ffffff' }}>combining different sounds</strong> and musical elements together.
              </p>
            </div>
            <div className="glass-panel rounded-2xl p-8 border text-center">
              <div className="text-5xl font-bold mb-4" style={{ color: '#D4AF37' }}>"Onics"</div>
              <p className="text-lg" style={{ color: '#e5e5e5' }}>
                Derived from the Greek word <em>"phonics"</em> meaning sound, highlighting the <strong style={{ color: '#ffffff' }}>technical aspects</strong> of music and sound production.
              </p>
            </div>
          </div>
          
          <div className="glass-panel rounded-2xl p-8 border mt-6 text-center" style={{ background: 'linear-gradient(135deg, rgba(58, 0, 136, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)' }}>
            <p className="text-xl" style={{ color: '#e5e5e5' }}>
              Together, <strong style={{ color: '#D4AF37' }}>Blendonics</strong> symbolises the harmonious blend of <span style={{ color: '#ffffff' }}>creativity</span> and <span style={{ color: '#ffffff' }}>technical expertise</span> in DJing.
            </p>
          </div>
        </section>

        {/* Social Media Section */}
        <section className="text-center mb-16">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
              <Users size={28} style={{ color: '#D4AF37' }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#ffffff' }}>Connect With Us</h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://instagram.com/blendonics" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 hover:shadow-lg"
              style={{ background: 'linear-gradient(45deg, #833AB4, #FD1D1D, #F77737)', color: '#ffffff' }}
              data-testid="blendonics-instagram-link"
            >
              <Instagram size={24} />
              @blendonics
            </a>
            <a 
              href="https://instagram.com/djelliottrock" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 hover:shadow-lg border-2"
              style={{ borderColor: '#D4AF37', color: '#D4AF37', background: 'transparent' }}
              data-testid="elliott-instagram-link"
            >
              <Instagram size={24} />
              @djelliottrock
            </a>
          </div>
          
          <p className="mt-8 text-sm" style={{ color: '#666' }}>
            Follow us for DJ tips, behind-the-scenes content, and community updates.
          </p>
        </section>

        {/* Contact Section */}
        <section className="text-center">
          <div className="glass-panel rounded-2xl p-8 md:p-12 border" style={{ background: 'linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(58, 0, 136, 0.15) 100%)' }}>
            <div className="flex items-center gap-3 mb-6 justify-center">
              <div className="p-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.1)' }}>
                <Mail size={28} style={{ color: '#D4AF37' }} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#ffffff' }}>Contact Us</h2>
            </div>
            
            <p className="text-lg mb-6" style={{ color: '#a3a3a3' }}>
              Have questions? We'd love to hear from you.
            </p>
            
            <a 
              href="mailto:contact@blendonics.com"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105"
              style={{ background: '#D4AF37', color: '#141414' }}
              data-testid="contact-email-link"
            >
              <Mail size={20} />
              contact@blendonics.com
            </a>
            
            <p className="mt-6 text-sm" style={{ color: '#666' }}>
              We typically respond within 24-48 hours.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
